/**
 * Field-trip parent-order elections — pure helpers behind the field-trip
 * dialog's "Enable parent orders" mode.
 *
 * The school picks a date + mealtime(s) and a scope (whole school, or
 * selected grades); parents of in-scope students then see the field trip meal
 * on that date beside the regular menu. Subscriptions follow the product
 * rule: grade scope → auto-ordered for those students; whole school → left
 * alone.
 */
import type { FieldTripElectionScopeKind, FieldTripElectionWire, FieldTripGroupScope } from '@/api/field-trips'
import type { Mealtime } from '@/lib/mealtime'

export type FieldTripMode = 'ORDER_FOR_STUDENTS' | 'ENABLE_PARENT_ORDERS'

/** Grades → GRADE scopes, de-duplicated, blanks dropped, vocabulary order kept. */
export function buildElectionGroupScopes(grades: readonly string[]): FieldTripGroupScope[] {
  const seen = new Set<string>()
  const out: FieldTripGroupScope[] = []
  for (const raw of grades) {
    const value = raw.trim()
    if (!value || seen.has(value.toLowerCase())) {
      continue
    }
    seen.add(value.toLowerCase())
    out.push({ dimension: 'GRADE', value })
  }
  return out
}

/** Toggle a grade in the selection, keeping vocabulary order. */
export function toggleGrade(selected: readonly string[], grade: string, vocabulary: readonly string[]): string[] {
  const next = new Set(selected)
  if (next.has(grade)) {
    next.delete(grade)
  } else {
    next.add(grade)
  }
  return vocabulary.filter((g) => next.has(g))
}

/** "Parents of grades 3, 4 can order the field trip meal for Tue, Oct 2." */
export function fieldTripElectionSummary(args: {
  scopeKind: FieldTripElectionScopeKind
  grades: readonly string[]
  dayLabel: string
}): string {
  const who =
    args.scopeKind === 'ALL'
      ? 'Parents of every student'
      : args.grades.length === 0
        ? 'Parents of the selected grades'
        : `Parents of grade${args.grades.length === 1 ? '' : 's'} ${args.grades.join(', ')}`
  return `${who} can order the field trip meal for ${args.dayLabel}.`
}

/** The subscriptions note under the summary — the product rule in one sentence. */
export function electionSubscriptionNote(scopeKind: FieldTripElectionScopeKind, fieldTripOnly = false): string {
  if (fieldTripOnly) {
    return scopeKind === 'GROUPS'
      ? 'All other menu items are hidden for these students that day, and their auto-orders will receive the field trip meal.'
      : 'All other menu items are hidden for every student that day, and auto-orders will receive the field trip meal.'
  }
  return scopeKind === 'GROUPS'
    ? 'Auto-orders for students in these grades will receive the field trip meal instead of the regular menu.'
    : 'Auto-orders are not changed — families order the field trip meal themselves.'
}

/** Whether the parent-order form can submit. */
export function electionFormValid(args: {
  serviceDate: string
  mealtimes: ReadonlySet<Mealtime>
  scopeKind: FieldTripElectionScopeKind
  grades: readonly string[]
}): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(args.serviceDate) || args.mealtimes.size === 0) {
    return false
  }
  return args.scopeKind === 'ALL' || args.grades.length > 0
}

/**
 * The live elections on or after `todayKey` (YYYY-MM-DD), soonest first then
 * by mealtime label order — the dialog's "Enabled parent ordering" history.
 */
export function upcomingElections(
  elections: ReadonlyArray<FieldTripElectionWire>,
  todayKey: string,
): FieldTripElectionWire[] {
  return elections
    .filter((e) => e.serviceDate >= todayKey)
    .sort((a, b) => a.serviceDate.localeCompare(b.serviceDate) || a.mealtime.localeCompare(b.mealtime))
}

/** The live elections for one date, keyed by mealtime. */
export function electionsForDate(
  elections: ReadonlyArray<FieldTripElectionWire>,
  serviceDate: string,
): Map<string, FieldTripElectionWire> {
  const out = new Map<string, FieldTripElectionWire>()
  for (const election of elections) {
    if (election.serviceDate === serviceDate) {
      out.set(election.mealtime, election)
    }
  }
  return out
}

/** "Whole school" / "Grades 3, 4" for an existing election row. */
export function electionScopeLabel(
  election: Pick<FieldTripElectionWire, 'scopeKind' | 'groupScopes'> &
    Partial<Pick<FieldTripElectionWire, 'fieldTripOnly'>>,
): string {
  const suffix = election.fieldTripOnly ? ' · Field trip meal only' : ''
  if (election.scopeKind === 'ALL') {
    return `Whole school${suffix}`
  }
  const grades = election.groupScopes.filter((s) => s.dimension === 'GRADE').map((s) => s.value)
  const others = election.groupScopes.filter((s) => s.dimension !== 'GRADE').map((s) => s.value)
  const parts: string[] = []
  if (grades.length > 0) {
    parts.push(`Grade${grades.length === 1 ? '' : 's'} ${grades.join(', ')}`)
  }
  if (others.length > 0) {
    parts.push(others.join(', '))
  }
  return `${parts.join(' · ') || 'Selected groups'}${suffix}`
}

/**
 * Toast copy for what a field-trip-only save did to orders already placed:
 * how many regular meals moved to the field trip meal, and how many it could
 * not move (past the ordering cutoff, placed by the school, special diet).
 * Null when nothing was touched, so the dialog stays quiet.
 */
export function switchedOrdersSummary(
  results: ReadonlyArray<{ switched: readonly unknown[]; notSwitched: readonly unknown[] } | null>,
): { title: string; description: string | null } | null {
  const switched = results.reduce((sum, r) => sum + (r?.switched.length ?? 0), 0)
  const notSwitched = results.reduce((sum, r) => sum + (r?.notSwitched.length ?? 0), 0)
  if (switched === 0 && notSwitched === 0) {
    return null
  }
  const plural = (n: number) => (n === 1 ? 'order' : 'orders')
  return {
    title:
      switched > 0
        ? `${switched} existing ${plural(switched)} switched to the field trip meal`
        : 'Some existing orders still have a regular meal',
    description:
      notSwitched > 0
        ? `${notSwitched} ${plural(notSwitched)} could not be switched (past the ordering deadline or placed by the school). Update ${notSwitched === 1 ? 'it' : 'them'} on the order sheet.`
        : null,
  }
}
